Mmg.addShortcode(
  'property_links',
  '.sc-property-links',

  /**
   * @param {Element} el
   * @param {object} model
   * @param {jQuery} $
   */
  (el, model, $) => {
    const $root = $(el);
    const pageSize = Math.max(1, parseInt(model.pageSize, 10) || 10);

    const $pagination = $root.find('[data-pl-pagination]');
    const $range = $pagination.find('.pagination__page-range');
    const $prev = $pagination.find('.pagination__link--prev');
    const $next = $pagination.find('.pagination__link--next');
    const $sort = $root.find('[data-pl-sort]');
    const $tabBtns = $root.find('[data-pl-tab]');
    const $empty = $root.find('[data-pl-empty]');
    const $list = $root.find('[data-pl-list]');

    const FILTER_KEYS = ['price_range', 'beds', 'home_type'];

    let activeTab = 'all';
    let sortMode = 'alpha';
    let pageIndex = 0;

    /**
     * @param {string|null} raw
     */
    function parseNullableFloat(raw) {
      const n = parseFloat(raw);
      return Number.isNaN(n) ? null : n;
    }

    /**
     * @param {string|null} raw
     */
    function parseNullableInt(raw) {
      if (raw === null || raw === '') {
        return null;
      }
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? null : n;
    }

    /**
     * @param {string|null} raw
     * @param {number} fallback
     */
    function parseIntOrDefault(raw, fallback) {
      const n = parseNullableInt(raw);
      return n === null ? fallback : n;
    }

    /**
     * @param {string|null|undefined} raw
     */
    function normalizedLower(raw) {
      return String(raw || '').toLowerCase();
    }

    function getItems() {
      return $root.find('[data-pl-item]').toArray();
    }

    function getFilterSelect(key) {
      const sel = `[data-pl-filter="${key}"]`;
      const el = $root.find(sel)[0];
      return /** @type {HTMLSelectElement|null} */ (el || null);
    }

    function getFilters() {
      const priceSelect = getFilterSelect('price_range');
      const bedsSelect = getFilterSelect('beds');
      const homeTypeSelect = getFilterSelect('home_type');

      return {
        priceRange: priceSelect ? String(priceSelect.value || '') : '',
        beds: bedsSelect ? String(bedsSelect.value || '') : '',
        homeType: homeTypeSelect ? String(homeTypeSelect.value || '') : '',
      };
    }

    /**
     * @param {Element} el
     */
    function itemPrice(el) {
      return parseNullableFloat(el.getAttribute('data-pl-price'));
    }

    /**
     * @param {Element} el
     */
    function itemOrder(el) {
      return parseIntOrDefault(el.getAttribute('data-pl-order'), 0);
    }

    /**
     * @param {Element} el
     */
    function itemAddressSort(el) {
      return normalizedLower(el.getAttribute('data-pl-address-sort')).trim();
    }

    /**
     * @param {Element} el
     */
    function itemBeds(el) {
      return parseNullableInt(el.getAttribute('data-pl-beds'));
    }

    /**
     * @param {Element} el
     */
    function itemHasVisiblePrice(el) {
      const amount = el.querySelector('[class*="__link-amount"]');
      if (!amount) {
        return false;
      }
      return String(amount.textContent || '').trim() !== '';
    }

    /**
     * @param {number|null|undefined} price
     * @param {string} rangeKey
     */
    function priceInSelectedRange(price, rangeKey) {
      if (!rangeKey) {
        return true;
      }
      if (price === null || typeof price === 'undefined') {
        return false;
      }
      switch (rangeKey) {
        case 'under_250k':
          return price < 250000;
        case '250k_500k':
          return price >= 250000 && price < 500000;
        case '500k_1m':
          return price >= 500000 && price < 1000000;
        case '1m_2m':
          return price >= 1000000 && price < 2000000;
        case '2m_plus':
          return price >= 2000000;
        default:
          return true;
      }
    }

    /**
     * @param {Element} node
     * @param {{ priceRange: string, beds: string, homeType: string }} filters
     */
    function itemMatchesNonTabFilters(node, filters) {
      const minBeds = parseNullableInt(filters.beds);
      const price = itemPrice(node);
      if (filters.priceRange !== '' && !itemHasVisiblePrice(node)) {
        return false;
      }
      if (!priceInSelectedRange(price, filters.priceRange)) {
        return false;
      }
      if (minBeds !== null) {
        const b = itemBeds(node);
        if (b === null || b < minBeds) {
          return false;
        }
      }
      if (filters.homeType !== '') {
        const itemHomeType = normalizedLower(
          node.getAttribute('data-pl-home-type')
        );
        if (itemHomeType !== normalizedLower(filters.homeType)) {
          return false;
        }
      }
      return true;
    }

    /**
     * @param {Element[]} items
     * @param {{ priceRange: string, beds: string, homeType: string }} filters
     */
    function applyNonTabFilters(items, filters) {
      return items.filter((node) => itemMatchesNonTabFilters(node, filters));
    }

    /**
     * Keep each filter option visible only when it can
     * produce at least one result in the current rendered list context.
     *
     * @param {Element[]} items
     */
    function syncFilterOptionVisibility(items) {
      const current = getFilters();
      const tabScopedItems = applyTab(items);

      FILTER_KEYS.forEach((key) => {
        const select = getFilterSelect(key);
        if (!select) {
          return;
        }

        let selectedIsInvalid = false;
        Array.from(select.options).forEach((opt) => {
          const value = String(opt.value || '');
          if (value === '') {
            opt.hidden = false;
            opt.disabled = false;
            return;
          }

          const candidate = {
            priceRange: current.priceRange,
            beds: current.beds,
            homeType: current.homeType,
          };
          if (key === 'price_range') {
            candidate.priceRange = value;
          } else if (key === 'beds') {
            candidate.beds = value;
          } else if (key === 'home_type') {
            candidate.homeType = value;
          }

          const hasMatch = tabScopedItems.some((node) =>
            itemMatchesNonTabFilters(node, candidate)
          );
          opt.hidden = !hasMatch;
          opt.disabled = !hasMatch;
          if (!hasMatch && select.value === value) {
            selectedIsInvalid = true;
          }
        });

        if (selectedIsInvalid) {
          select.value = '';
        }
      });
    }

    /**
     * @param {Element[]} items
     */
    function applyTab(items) {
      if (activeTab === 'all') {
        return items.slice();
      }
      return items.filter((node) => {
        return node.getAttribute('data-pl-status') === activeTab;
      });
    }

    /**
     * @param {Element} a
     * @param {Element} b
     * @param {boolean} asc
     */
    function comparePrice(a, b, asc) {
      const ap = itemPrice(a);
      const bp = itemPrice(b);
      if (ap === null && bp === null) {
        return 0;
      }
      if (ap === null) {
        return 1;
      }
      if (bp === null) {
        return -1;
      }
      const cmp = ap - bp;
      return asc ? cmp : -cmp;
    }

    /**
     * @param {Element[]} items
     */
    function sortItems(items) {
      const out = items.slice();
      if (sortMode === 'price_asc') {
        out.sort((a, b) => comparePrice(a, b, true));
      } else if (sortMode === 'price_desc') {
        out.sort((a, b) => comparePrice(a, b, false));
      } else if (sortMode === 'alpha') {
        out.sort((a, b) => {
          const cmp = itemAddressSort(a).localeCompare(itemAddressSort(b));
          return cmp !== 0 ? cmp : itemOrder(a) - itemOrder(b);
        });
      } else {
        out.sort((a, b) => itemOrder(a) - itemOrder(b));
      }
      return out;
    }

    /**
     * @param {jQuery} $link
     * @param {boolean} disabled
     */
    function setPaginationLinkDisabled($link, disabled) {
      $link.toggleClass('disabled', disabled);
      if (disabled) {
        $link.attr({ 'aria-disabled': 'true', tabindex: '-1' });
      } else {
        $link.removeAttr('aria-disabled').removeAttr('tabindex');
      }
    }

    function render() {
      const items = getItems();
      syncFilterOptionVisibility(items);

      const filters = getFilters();
      let pipe = applyNonTabFilters(items, filters);
      pipe = applyTab(pipe);
      pipe = sortItems(pipe);

      const total = pipe.length;
      const pages = Math.max(1, Math.ceil(total / pageSize));
      if (pageIndex > pages - 1) {
        pageIndex = Math.max(0, pages - 1);
      }
      const start = pageIndex * pageSize;
      const pageSlice = pipe.slice(start, start + pageSize);
      const gridRows = Math.max(1, Math.ceil(pageSlice.length / 2));
      const visibleSet = new Set(pageSlice);
      const pipeIndex = new Map(pipe.map((node, i) => [node, i]));

      $list.css('--pl-grid-rows', String(gridRows));

      items.forEach((node) => {
        const i = pipeIndex.get(node);
        if (i === undefined) {
          node.style.order = '99999';
          node.hidden = true;
          return;
        }
        node.style.order = String(i);
        node.hidden = !visibleSet.has(node);
      });

      const hi = total === 0 ? 0 : start + 1;
      const lo = total === 0 ? 0 : Math.min(start + pageSlice.length, total);
      $range.text(`${hi}–${lo} of ${total}`);

      $pagination.prop('hidden', total === 0);

      setPaginationLinkDisabled($prev, pageIndex <= 0);
      setPaginationLinkDisabled($next, pageIndex >= pages - 1 || total === 0);

      const emptyEl = $empty[0];
      if (emptyEl) {
        if (total === 0) {
          emptyEl.hidden = false;
          emptyEl.textContent =
            items.length > 0
              ? 'No listings match your filters.'
              : 'No listings to display.';
        } else {
          emptyEl.hidden = true;
        }
      }
    }

    $tabBtns.on('click', function onTab(ev) {
      ev.preventDefault();
      const tab = $(this).data('pl-tab');
      if (!tab) {
        return;
      }
      activeTab = tab;
      pageIndex = 0;
      $tabBtns.removeClass('active').attr('aria-selected', 'false');
      $(this).addClass('active').attr('aria-selected', 'true');
      render();
    });

    $root.on('change input', '[data-pl-filter]', () => {
      pageIndex = 0;
      render();
    });

    $sort.on('change', () => {
      sortMode = String($sort.val() || 'alpha');
      pageIndex = 0;
      render();
    });

    $prev.on('click', (ev) => {
      ev.preventDefault();
      if (pageIndex > 0) {
        pageIndex -= 1;
        render();
      }
    });

    $next.on('click', (ev) => {
      ev.preventDefault();
      pageIndex += 1;
      render();
    });

    render();
  }
);

